import Hls from 'hls.js';

/**
 * Chargeur de clé personnalisé pour Hls.js
 * Permet d'injecter manuellement la clé déchiffrée dans le flux
 */
export class CustomHlsKeyLoader extends Hls.DefaultConfig.loader.prototype.constructor {
    constructor(config, sessionKey, backendUrl) {
        super(config);
        this.sessionKey = sessionKey;
        this.backendUrl = backendUrl;
    }

    load(context, config, callbacks) {
        // La clé est déjà obtenue de manière sûre avec les détails de session.
        // L'hôte indiqué dans un manifeste peut varier (www, api ou ancien
        // domaine) après une migration de domaine. Hls.js ne renseigne pas
        // systématiquement context.type à "key", donc on identifie l'URI HLS
        // exacte, sans dépendre du sous-domaine.
        const keyPath = new URL(context.url, window.location.origin).pathname;
        const isKeyRequest = /(^|\/)cle\.key$/i.test(keyPath) || /(^|\/)hls\.key$/i.test(keyPath);

        if (isKeyRequest) {
            if (this.sessionKey) {
                callbacks.onSuccess({
                    url: context.url,
                    data: this.sessionKey.buffer,
                    responseType: 'arraybuffer',
                    responseUrl: context.url
                }, null, context);
            } else {
                callbacks.onError({ code: 0, text: "Clé non disponible" }, context);
            }
        } else {
            super.load(context, config, callbacks);
        }
    }
}

/**
 * Initialise le moteur HLS et lie la vidéo
 */
export function initHlsPlayer(videoEl, streamUrl, sessionKey, backendUrl, onLevelLoaded, accessHeaders = {}) {
    const headerEntries = Object.entries(accessHeaders || {});
    // Lien v2 : l'accès passe par un en-tête et la clé ne vient jamais du serveur.
    // Le lecteur natif de Safari ne permet ni l'un ni l'autre : hls.js obligatoire
    // (MSE, ou ManagedMediaSource sur iOS ≥ 17.1).
    if (headerEntries.length > 0 && !Hls.isSupported()) {
        console.warn("[HLS] Lecture vidéo indisponible sur ce navigateur pour un lien chiffré de bout en bout.");
        return null;
    }
    if (Hls.isSupported()) {
        const hls = new Hls({
            startPosition: 0,
            lowLatencyMode: false,
            // Buffer de sécurité de ~12s (4 segments) pour garantir une lecture continue et fluide
            liveSyncDurationCount: 4,
            liveMaxLatencyDurationCount: Infinity,
            maxBufferLength: 60,
            maxMaxBufferLength: 120,
            backBufferLength: 90,
            enableWorker: true,
            highBufferWatchdogPeriod: 3,
            nudgeMaxRetry: 5,
            xhrSetup: headerEntries.length > 0
                ? (xhr) => headerEntries.forEach(([name, value]) => xhr.setRequestHeader(name, value))
                : undefined,
            loader: function (config) {
                return new CustomHlsKeyLoader(config, sessionKey, backendUrl);
            }
        });

        hls.loadSource(streamUrl);
        hls.attachMedia(videoEl);

        if (onLevelLoaded) {
            hls.on(Hls.Events.LEVEL_LOADED, onLevelLoaded);
        }

        return hls;
    } else if (videoEl && videoEl.canPlayType && videoEl.canPlayType('application/vnd.apple.mpegurl')) {
        // Fallback natif Safari (iOS / WebKit)
        console.log("[HLS] Mode natif WebKit / iOS détecté pour:", streamUrl);
        videoEl.src = streamUrl;
        videoEl.muted = true;
        videoEl.defaultMuted = true;
        videoEl.setAttribute('playsinline', '');
        videoEl.setAttribute('webkit-playsinline', 'true');
        videoEl.setAttribute('muted', '');
        videoEl.load();

        const onReadyToPlay = () => {
            if (videoEl.paused) {
                videoEl.play().catch(e => {
                    console.log("[HLS] Play en attente d'interaction utilisateur:", e.message);
                });
            }
        };

        videoEl.addEventListener('loadedmetadata', onReadyToPlay, { once: true });
        videoEl.addEventListener('canplay', onReadyToPlay, { once: true });
        return null;
    }
    return null;
}

/**
 * Configure le routage audio (GainNode)
 */
export function setupAudioRouting(videoEl, initialVolume) {
    try {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (!AudioContextClass) {
            if (videoEl) videoEl.volume = initialVolume / 100;
            return { audioCtx: null, gainNode: null, source: null };
        }
        const audioCtx = new AudioContextClass();
        // Sur iOS Safari, createMediaElementSource sur un élément HLS peut bloquer la lecture
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) || /iPad|iPhone|iPod/.test(navigator.userAgent);
        if (isSafari) {
            if (videoEl) videoEl.volume = initialVolume / 100;
            return { audioCtx, gainNode: null, source: null };
        }

        const source = audioCtx.createMediaElementSource(videoEl);
        const gainNode = audioCtx.createGain();

        source.connect(gainNode).connect(audioCtx.destination);
        gainNode.gain.value = initialVolume / 100;

        return { audioCtx, gainNode, source };
    } catch (e) {
        console.warn("[AUDIO] Web Audio routing non disponible, fallback volume natif:", e);
        if (videoEl) videoEl.volume = initialVolume / 100;
        return { audioCtx: null, gainNode: null, source: null };
    }
}
